// src/components/templates/TemplateReviewModal.tsx
import React from 'react';
import { FiX } from 'react-icons/fi';
import { UserDefinedTemplate } from '../../features/templates/types';

const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface TemplateReviewModalProps {
  template: UserDefinedTemplate;
  isOpen: boolean;
  onClose: () => void;
  onApprove: () => void;
  onReject: () => void;
  isUpdating?: boolean;
}

const TemplateReviewModal: React.FC<TemplateReviewModalProps> = ({
  template,
  isOpen,
  onClose,
  onApprove,
  onReject,
  isUpdating = false,
}) => {
  if (!isOpen || !template) return null;

  const html = template.html || '';
  const css = template.css || '';

  const templateName = template.name || 'Untitled Template';
  const shouldTruncate = templateName.length > 20;
  const displayedName = shouldTruncate
    ? `${templateName.substring(0, 20)}...`
    : templateName;

  return (
    <div className='fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4'>
      <div className='bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden'>
        {/* Header */}
        <div className='bg-white px-6 py-4 flex items-center justify-between border-b'>
          <div className='flex items-center gap-6'>
            <h2 className='text-lg font-semibold text-gray-800'>
              Preview template
            </h2>

            {/* Truncated Name with Tooltip (now below) */}
            <div className='group relative'>
              <span className='text-sm text-gray-500 max-w-md'>
                {displayedName}
              </span>

              {shouldTruncate && (
                <div className='absolute left-0 top-full mt-2 px-3 py-2 bg-gray-800 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-10 shadow-lg'>
                  {templateName}
                  {/* Tooltip arrow pointing upward */}
                  <svg
                    className='absolute bottom-full left-4 w-3 h-3 fill-gray-800'
                    viewBox='0 0 10 10'
                  >
                    <polygon points='0,10 10,10 5,5' />
                  </svg>
                </div>
              )}
            </div>
          </div>

          <div className='flex items-center gap-3'>
            <button
              onClick={onReject}
              disabled={isUpdating}
              className='px-4 py-2 text-sm font-medium text-[#00A4BD] border border-[#00A4BD] rounded-md hover:bg-cyan-50 transition disabled:opacity-60'
            >
              Reject
            </button>

            <button
              onClick={onApprove}
              disabled={isUpdating}
              className='px-4 py-2 text-sm font-medium text-white bg-[#00A4BD] rounded-md hover:bg-cyan-700 transition disabled:opacity-60'
            >
              Approve
            </button>

            <button
              onClick={onClose}
              className='ml-2 text-gray-400 hover:text-gray-600'
            >
              <FiXComponent size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className='flex-1 bg-[#E1EAF0] overflow-auto'>
          <div className='min-h-full flex items-center justify-center p-8'>
            <div className='bg-white rounded-lg shadow-md w-full max-w-3xl overflow-hidden'>
              <style dangerouslySetInnerHTML={{ __html: css }} />

              <div
                dangerouslySetInnerHTML={{
                  __html:
                    html ||
                    `<div class="p-16 text-center text-gray-400">
                      <p>No content available yet.</p>
                      <p class="text-sm mt-2">Start editing…</p>
                    </div>`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateReviewModal;
